import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Download, Printer, Loader2, ExternalLink } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useChartOfAccounts } from '@/hooks/queries/useAccounting';
import { useGeneralLedger } from '@/hooks/queries/useAccountingReports';
import { useState } from 'react';
import { JOURNAL_TYPE_LABELS } from '@/types/accounting';

export const Route = createFileRoute('/apps/accounting/reports/general-ledger')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: GeneralLedgerPage,
});

function GeneralLedgerPage() {
  const { company } = useAuthStore();
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const { data: accounts = [] } = useChartOfAccounts(company?.id || '', {
    isActive: true,
  });

  const { data: ledgerEntries = [], isLoading } = useGeneralLedger(
    company?.id || '',
    selectedAccountId,
    {
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }
  );

  const selectedAccount = accounts.find((acc) => acc.id === selectedAccountId);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    alert('Export Excel sera disponible bientôt');
  };

  // Filter accounts with movements
  const accountsWithMovements = accounts.filter((acc) => acc.current_balance !== 0);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">دفتر الأستاذ / Grand livre</h1>
            <p className="text-muted-foreground mt-2">
              Détail des mouvements par compte / تفاصيل الحركات حسب الحساب
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handlePrint} disabled={!selectedAccountId}>
              <Printer className="w-4 h-4 mr-2" />
              Imprimer
            </Button>
            <Button variant="outline" onClick={handleExport} disabled={!selectedAccountId}>
              <Download className="w-4 h-4 mr-2" />
              Exporter Excel
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Sélection du compte / اختيار الحساب</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Compte / الحساب *</label>
                <Select value={selectedAccountId} onValueChange={setSelectedAccountId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un compte..." />
                  </SelectTrigger>
                  <SelectContent>
                    <div className="p-2 text-xs text-muted-foreground border-b">
                      Comptes avec mouvements ({accountsWithMovements.length})
                    </div>
                    {accountsWithMovements.map((acc) => (
                      <SelectItem key={acc.id} value={acc.id}>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold">{acc.account_code}</span>
                          <span className="text-sm">{acc.account_name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({formatCurrency(acc.current_balance)} DA)
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Date de début</label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Date de fin</label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Info */}
        {selectedAccount && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-blue-700">Code compte</p>
                  <p className="text-lg font-bold font-mono text-blue-900">
                    {selectedAccount.account_code}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-xs text-blue-700">Libellé</p>
                  <p className="text-base font-medium text-blue-900">
                    {selectedAccount.account_name}
                  </p>
                  {selectedAccount.account_name_ar && (
                    <p className="text-sm text-blue-700" dir="rtl">
                      {selectedAccount.account_name_ar}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-blue-700">Solde actuel</p>
                  <p className="text-lg font-bold text-blue-900">
                    {formatCurrency(selectedAccount.current_balance)} DA
                  </p>
                  <Badge
                    variant="outline"
                    className="mt-1 bg-blue-100 text-blue-700 border-blue-300"
                  >
                    {selectedAccount.nature === 'debit' ? 'Débiteur' : 'Créditeur'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* General Ledger Table */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : !selectedAccountId ? (
              <div className="text-center py-12">
                <p className="text-lg font-medium">Sélectionnez un compte</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Choisissez un compte dans la liste pour afficher ses mouvements
                </p>
              </div>
            ) : ledgerEntries.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-lg font-medium">Aucun mouvement trouvé</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Ce compte n'a pas de mouvements pour la période sélectionnée
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="font-bold">Date</TableHead>
                      <TableHead className="font-bold">N° Écriture</TableHead>
                      <TableHead className="font-bold">Journal</TableHead>
                      <TableHead className="font-bold">Libellé</TableHead>
                      <TableHead className="font-bold text-right">Débit</TableHead>
                      <TableHead className="font-bold text-right">Crédit</TableHead>
                      <TableHead className="font-bold text-right">Solde</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ledgerEntries.map((entry, index) => {
                      const journalLabel =
                        JOURNAL_TYPE_LABELS[entry.journal_type as keyof typeof JOURNAL_TYPE_LABELS];

                      return (
                        <TableRow key={`${entry.entry_id}-${index}`}>
                          <TableCell className="font-mono">
                            {formatDate(entry.entry_date)}
                          </TableCell>
                          <TableCell className="font-mono font-bold">
                            {entry.entry_number}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {journalLabel?.fr || entry.journal_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-xs truncate">
                            {entry.description || '-'}
                          </TableCell>
                          <TableCell className="text-right font-mono text-blue-600">
                            {entry.debit > 0 ? formatCurrency(entry.debit) : '-'}
                          </TableCell>
                          <TableCell className="text-right font-mono text-green-600">
                            {entry.credit > 0 ? formatCurrency(entry.credit) : '-'}
                          </TableCell>
                          <TableCell className="text-right font-mono font-bold">
                            {formatCurrency(entry.balance)}
                          </TableCell>
                          <TableCell>
                            <Link to={`/apps/accounting/entries/${entry.entry_id}`}>
                              <Button variant="ghost" size="sm">
                                <ExternalLink className="w-3 h-3" />
                              </Button>
                            </Link>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary */}
        {ledgerEntries.length > 0 && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-green-700 font-medium">Nombre de mouvements</p>
                  <p className="text-2xl font-bold text-green-900">{ledgerEntries.length}</p>
                </div>
                <div>
                  <p className="text-green-700 font-medium">Total Débit</p>
                  <p className="text-2xl font-bold text-green-900">
                    {formatCurrency(
                      ledgerEntries.reduce((sum, e) => sum + e.debit, 0)
                    )}{' '}
                    DA
                  </p>
                </div>
                <div>
                  <p className="text-green-700 font-medium">Total Crédit</p>
                  <p className="text-2xl font-bold text-green-900">
                    {formatCurrency(
                      ledgerEntries.reduce((sum, e) => sum + e.credit, 0)
                    )}{' '}
                    DA
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
